import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

import useAnimate from '../ui/Motion';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   A stage and a playlist.

   The portfolio's gallery is a feed: untitled tiles, and a viewer that
   covers the page. This is a library. Every clip has a name and a
   reason to watch it, so the names stay on screen beside the player
   and choosing one plays it in place.

   A clip comes from a file under public/ (`src`) or from YouTube
   (`youtube`, the video id). One with neither is a slot held for a
   clip that is not ready yet, and is drawn as one rather than as a
   player that cannot play.
   ───────────────────────────────────────────────────────────── */

const ready = clip => Boolean(clip.src || clip.youtube);

/* YouTube serves a still for every video, so a clip hosted there needs
   no poster of its own. */
const posterOf = clip =>
    clip.poster || (clip.youtube ? `https://i.ytimg.com/vi/${clip.youtube}/hqdefault.jpg` : null);

function PlayGlyph() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.14v13.72a.5.5 0 00.77.42l10.29-6.86a.5.5 0 000-.84L8.77 4.72a.5.5 0 00-.77.42z" />
        </svg>
    );
}

function Player({ clip, autoPlay }) {
    const videoRef = useRef(null);

    /* The gallery viewer's teardown, for the same reason: detaching a
       playing <video> does not reliably stop it, so the clip being
       replaced is paused and made to let go of its stream. */
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return undefined;

        return () => {
            el.pause();
            el.removeAttribute('src');
            el.load();
        };
    }, []);

    if (clip.youtube) {
        return (
            <iframe
                className="reel__media"
                src={`https://www.youtube-nocookie.com/embed/${clip.youtube}?rel=0${autoPlay ? '&autoplay=1' : ''}`}
                title={clip.title}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
            />
        );
    }

    /* Until someone asks for the clip, its poster stands in and none of the
       video is fetched, so the page costs nothing extra for a reader who
       never presses play. Without a poster the browser reads the index to
       paint a first frame, which scripts/encode-video.sh keeps at the front
       of the file and a few hundred KB at most. */
    if (clip.src) {
        return (
            <video
                ref={videoRef}
                className="reel__media"
                src={clip.src}
                poster={clip.poster}
                controls
                playsInline
                preload={autoPlay ? 'auto' : clip.poster ? 'none' : 'metadata'}
                autoPlay={autoPlay}
            />
        );
    }

    return (
        <div className="reel__pending">
            <span className="reel__glyph"><PlayGlyph /></span>
            <span className="t-eyebrow t-eyebrow--accent">Coming soon</span>
        </div>
    );
}

export default function VideoReel({ clips }) {
    const [index, setIndex] = useState(0);
    const [chosen, setChosen] = useState(false);
    const stageRef = useRef(null);
    const animate = useAnimate();

    const clip = clips[index];

    /* The first clip waits for a press. Once the reader has picked one
       from the list they have asked to watch it, so it starts on its own. */
    const choose = i => {
        setIndex(i);
        setChosen(true);

        /* On a phone the list runs on below the player, so a clip picked
           from the bottom of it would start playing off screen. `nearest`
           does nothing when the stage is already in view, which on a
           desktop, where it is sticky, is always. */
        stageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    return (
        <div className="reel">
            <div className="reel__stage" ref={stageRef}>
                <div className="reel__frame">
                    <Player key={clip.id} clip={clip} autoPlay={chosen} />
                </div>
            </div>

            {/* What is playing heads the column beside the player rather than
                sitting under it, so a short list still fills that column and
                a long one starts with the answer to "what am I watching". */}
            <div className="reel__side">
                {/* The live region has to outlive the clip it describes, or
                    a screen reader never hears that the clip changed. */}
                <div className="reel__now" aria-live="polite">
                    <motion.div
                        key={clip.id}
                        initial={animate ? { opacity: 0, y: 8 } : false}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                    >
                        <span className="reel__count">
                            {String(index + 1).padStart(2, '0')} <i>/</i> {String(clips.length).padStart(2, '0')}
                        </span>
                        <h3 className="t-h3 reel__title">{clip.title}</h3>
                        {clip.caption && <p className="t-body reel__caption">{clip.caption}</p>}
                    </motion.div>
                </div>

                <ol className="reel__list" aria-label="Videos">
                    {clips.map((item, i) => {
                        const poster = posterOf(item);
                        const current = i === index;

                        return (
                            <motion.li
                                key={item.id}
                                initial={animate ? { opacity: 0, y: 18 } : false}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.4 }}
                                transition={{ duration: 0.6, delay: Math.min(i * 0.05, 0.3), ease: EASE }}
                            >
                                <button
                                    type="button"
                                    className={`reel__item${current ? ' reel__item--current' : ''}`}
                                    aria-current={current ? 'true' : undefined}
                                    onClick={() => choose(i)}
                                >
                                    <span className="reel__thumb">
                                        {poster
                                            ? <img src={poster} alt="" loading="lazy" decoding="async" />
                                            : <PlayGlyph />}
                                    </span>

                                    <span className="reel__item-body">
                                        <span className="reel__item-num">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="reel__item-title">{item.title}</span>
                                        <span className="reel__item-meta">
                                            {ready(item) ? item.length || 'Watch' : 'Coming soon'}
                                        </span>
                                    </span>
                                </button>
                            </motion.li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}
